import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import {
  DRAWER_WIDTH,
  LoadingError,
  LoadingScreen,
  MainAppBar,
  settingUrl,
  useOnlineStatus,
  useSettings,
} from "@mbits/mray-react-common";
import logo from "../assets/mray_importer_logo.svg";
import { t } from "i18next";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import LaunchIcon from "@mui/icons-material/Launch";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { useAuth, type AuthContextProps } from "react-oidc-context";
import {
  getAccessibleResourceIdsFromClaims,
  getUserProjectClaimKeys,
  isAdmin,
  isMtra,
} from "../utils/auth.ts";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import DriveFolderUploadOutlinedIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { type JSX, useCallback, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import useAuthSessionManagement from "@/hooks/useAuthSessionManagement";
import { apiClient, setAccessToken } from "@/api/http";
import { setAccessToken as setScanLibAccessToken } from "@mbits/mray-scan-lib";
import { buildSigninStateFromLocation } from "@/auth";
import { SETTINGS_NAV } from "@/pages/settings/nav";

/**
 * RootComponent
 *
 * The main layout component of the application.
 * It handles global UI elements, authentication, and developer tools.
 *
 * Responsibilities:
 * - Displays the main application AppBar (`MainAppBar`) including drawer, user menu, and app switcher.
 * - Checks user authentication using `useAuth` and triggers redirect sign-in when needed.
 *
 * @component
 * @example
 * return <RootComponent />;
 */

export default function RootComponent() {
  const auth = useAuth();
  const admin = isAdmin(auth.user?.profile);
  const mtra = isMtra(auth.user?.profile);
  const navigate = useNavigate();
  const currentPath = useRouterState({ select: (s) => s.location.pathname });

  useAuthSessionManagement(auth, {
    onAccessTokenChanged: useCallback((token?: string) => {
      setAccessToken(token);
      // Keep the @mbits/mray-scan-lib internal axios client (used by QRCard) authorized.
      setScanLibAccessToken(token);
    }, []),
    buildSigninState: useCallback(
      () => buildSigninStateFromLocation(window.location),
      [],
    ),
  });

  const drawerItems = getDrawerItems(admin, mtra, navigate, currentPath);

  // Legal links come from the backend. GET /settings only needs an
  // authenticated user, so this works for every role, not just admins.
  const { data: settings } = useSettings(window.ENV.importerApiUrl, apiClient);
  const privacyUrl = settingUrl(settings, "privacy_policy");
  const termsUrl = settingUrl(settings, "terms_of_service");

  // On settings pages the drawer becomes a persistent side rail with the
  // content shifted right. Narrow viewports keep the temporary overlay, where
  // a pinned 240px rail would leave too little room.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const pinnedDrawer =
    currentPath.startsWith("/settings") && drawerItems.length > 0 && isDesktop;

  // Shift only while the pinned rail is actually open, so collapsing it reflows
  // to full width and the temporary overlay never offsets content.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const offsetContent = pinnedDrawer && drawerOpen;

  return (
    <>
      <MainAppBar
        appIcon={logo}
        isOnline={useOnlineStatus()}
        onAppIconClick={() => navigate({ to: "/" })}
        appSwitcherItems={getAppSwitcherItems(auth)}
        userMenuItems={getUserMenuItems(auth, privacyUrl, termsUrl)}
        drawerItems={drawerItems}
        pinnedDrawer={pinnedDrawer}
        onDrawerOpenChange={setDrawerOpen}
      />

      <Box
        sx={{
          ml: offsetContent ? `${DRAWER_WIDTH}px` : 0,
          transition: (theme) =>
            theme.transitions.create("margin", {
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}
      >
        {getAuthenticationResult(auth)}
      </Box>

      <TanStackRouterDevtools />
    </>
  );
}

// Reads app version from vite.config.ts
const version = __APP_VERSION__;

/**
 * Renders the appropriate authentication-related UI based on the user's status.
 *
 * @returns {JSX.Element}
 *
 * @example
 * getAuthenticationResult(auth)
 */
function getAuthenticationResult(auth: AuthContextProps): JSX.Element {
  // Loading
  if (auth.isLoading || auth.activeNavigator === "signinRedirect") {
    return <LoadingScreen />;
  }

  // Error
  if (!auth.isAuthenticated) {
    // Only show auth error screen for actual login errors, not silent renew
    // failures. Silent renew failures are retried on reconnect.
    if (auth.error) {
      return (
        <LoadingError
          title={t("App.authError.title")}
          message={t("App.authError.message")}
          refresh={() => {
            const state = buildSigninStateFromLocation(window.location);
            auth.signinRedirect(state !== undefined ? { state } : undefined);
          }}
          refreshLabel={t("App.authError.label")}
        />
      );
    }

    return <LoadingScreen />;
  }

  // Success: Render the child component, i.e. the page determined by router
  return <Outlet />;
}

// Returns the appropriate app switcher items for the app bar
function getAppSwitcherItems(auth: AuthContextProps) {
  const userProjectClaims = getUserProjectClaimKeys(auth.user?.profile);
  const userResourceIds = getAccessibleResourceIdsFromClaims(userProjectClaims);
  const accessibleApps = window.ENV.appSwitcher.filter((app) =>
    userResourceIds.includes(app.resourceId),
  );
  return accessibleApps;
}

// Returns the appropriate drawer items for the app bar
function getDrawerItems(
  admin: boolean,
  mtra: boolean,
  navigate: ReturnType<typeof useNavigate>,
  currentPath: string,
) {
  if (!admin && !mtra) return [];

  const drawerItems = [
    {
      label: t("drawer.upload"),
      icon: <DriveFolderUploadOutlinedIcon />,
      onClick: () => {
        navigate({ to: "/" });
      },
      selected: currentPath === "/",
    },
    {
      label: t("drawer.tasks"),
      icon: <TaskOutlinedIcon />,
      onClick: () => {
        navigate({ to: "/tasks", search: { highlightUid: undefined } });
      },
      selected: currentPath === "/tasks",
    },
    ...(admin
      ? [
          {
            label: t("drawer.worklists"),
            icon: <ListAltOutlinedIcon />,
            onClick: () => {
              navigate({ to: "/worklists" });
            },
            selected: currentPath === "/worklists",
          },
          {
            label: t("drawer.destinations"),
            icon: <SendOutlinedIcon />,
            onClick: () => {
              navigate({ to: "/destinations" });
            },
            selected: currentPath === "/destinations",
          },
          {
            label: t("drawer.dicomStore"),
            icon: <StorageOutlinedIcon />,
            onClick: () => {
              navigate({ to: "/dicom-store" });
            },
            selected: currentPath === "/dicom-store",
          },
          {
            label: t("drawer.settings"),
            icon: <SettingsOutlinedIcon />,
            selected: currentPath.startsWith("/settings"),
            children: SETTINGS_NAV.map(({ key, labelKey, icon: Icon }) => ({
              label: t(labelKey),
              icon: <Icon fontSize="small" />,
              onClick: () =>
                navigate({
                  to: "/settings/$settingKey",
                  params: { settingKey: key },
                }),
              selected: currentPath === `/settings/${key}`,
            })),
          },
        ]
      : []),
  ];

  return drawerItems;
}

// Returns the appropriate user menu items for the app bar
function getUserMenuItems(
  auth: AuthContextProps,
  privacyUrl: string | undefined,
  termsUrl: string | undefined,
) {
  const userMenuItems = [
    {
      label: t("appBar.logout"),
      icon: <LogoutIcon />,
      onClick: () => auth.signoutRedirect(),
    },
    {
      label: t("appBar.dataPrivacy"),
      icon: <GppMaybeOutlinedIcon />,
      href: privacyUrl,
    },
    {
      label: t("appBar.termsOfUse"),
      icon: <LaunchIcon />,
      href: termsUrl,
    },
    {
      label: t("appBar.imprint"),
      icon: <LaunchIcon />,
      href: window.ENV.imprintUrl,
    },
    {
      label: `Version: ${version}`,
      icon: null,
    },
  ];
  return userMenuItems;
}
